import { Column } from 'react-table';
import { Popup } from 'semantic-ui-react';
import { Button, Icon } from '@britishcouncil/react-admin';

import {
  EorMonitoringReasonEnum,
  EorMonitoringTypeEnum,
  EorSpeakingMonitoring,
  EorSpeakingMonitoringGroup,
} from 'api';
import { formatDate } from 'utils';
import { ActionsCell, CheckIcon } from 'components';
import { EorSpeakingMonitoringActions } from './EorSpeakingMonitoringActions';

const displayMonitoringType = (monitoringGroup: EorSpeakingMonitoringGroup): string => {
  switch (monitoringGroup.type.id) {
    case EorMonitoringTypeEnum.Q2:
    case EorMonitoringTypeEnum.Q4:
      return `${monitoringGroup.type.name} / ${monitoringGroup.typeYear}`;

    case EorMonitoringTypeEnum.PnsRequests:
    case EorMonitoringTypeEnum.InvestigationRequests:
      if (monitoringGroup.typeDate) {
        const date = new Date(monitoringGroup.typeDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${monitoringGroup.type.name} / ${month}.${year}`;
      }
      return monitoringGroup.type.name;

    case EorMonitoringTypeEnum.Other:
      return `${monitoringGroup.type.name} / ${monitoringGroup.typeComment}`;

    default:
      return '-';
  }
};

export const columns: Column<EorSpeakingMonitoringGroup>[] = [
  {
    Header: '',
    id: 'expander',
    width: '50px',
    Cell: ({ row }) => {
      const hasMultipleMonitorings = row.original.monitorings.length > 1;

      if (!hasMultipleMonitorings) {
        return null;
      }

      return (
        <ActionsCell>
          <Button icon basic compact size="mini" circular {...row.getToggleRowExpandedProps()}>
            <Icon name={row.isExpanded ? 'angle up' : 'angle down'} />
          </Button>
        </ActionsCell>
      );
    },
  },
  {
    Header: 'Date Of Monitoring',
    accessor: 'monitoringDate',
    width: 'auto',
    Cell: ({ row }) => <>{formatDate(row.original.monitoringDate)}</>,
  },
  {
    Header: 'Monitoring Reason',
    accessor: 'reason',
    width: 'auto',
    Cell: ({ row }) =>
      row.original.reason.id === EorMonitoringReasonEnum.Other ? (
        <Popup
          trigger={<span>{row.original.reason.name}</span>}
          content="For more information use details button"
          position="top center"
        />
      ) : (
        <span>{row.original.reason.name}</span>
      ),
  },
  {
    Header: 'Monitoring Type',
    id: 'type',
    width: 'auto',
    Cell: ({ row }) => <>{displayMonitoringType(row.original)}</>,
  },
  {
    Header: 'Stage',
    id: 'stage',
    width: 'auto',
    Cell: ({ row }) =>
      renderStageValues(row.original, row.isExpanded, (monitoring) => monitoring.stage.name),
  },
  {
    Header: 'Monitoring Result',
    id: 'result',
    width: 'auto',
    Cell: ({ row }) =>
      renderStageValues(row.original, row.isExpanded, (monitoring) => monitoring.result.name),
  },
  {
    Header: 'Follow Up Completed',
    accessor: 'followUpId',
    width: '75px',
    Cell: ({ row }) => <CheckIcon value={row.original.followUpId} />,
  },
  {
    Header: 'Details',
    id: 'actions',
    width: '120px',
    Cell: ({ row }) => <EorSpeakingMonitoringActions monitoringGroup={row.original} />,
  },
];

const renderStageValues = (
  row: EorSpeakingMonitoringGroup,
  isExpanded: boolean,
  getStageValue: (monitoring: EorSpeakingMonitoring) => string
) => {
  const sortedMonitorings = [...row.monitorings].sort((a, b) => b.stage.id - a.stage.id);

  if (!isExpanded) {
    return <>{getStageValue(sortedMonitorings[0])}</>;
  } else {
    return (
      <>
        {sortedMonitorings.map((monitoring) => (
          <p key={monitoring.id}>{getStageValue(monitoring)}</p>
        ))}
      </>
    );
  }
};
